import { useAtomValue } from 'jotai'
import { connectionStatusAtom } from '~/atoms/ConnectionState'
import { useOnlineStatus } from '~/hooks/useOnlineStatus'
import {
  registrationErrorAtom,
  registrationStatusAtom,
  transportConnectionStateAtom,
} from '~/hooks/usePhone'

import {
  type HealthInput,
  networkHealth,
  overallHealth,
  phoneServiceHealth,
  realtimeHealth,
  worstLevel,
} from './statusHealth'
import { useAppUpdatesHealth } from './useAppUpdatesHealth'
import { type HealthLevel } from './useDiagnosticsSnapshot'

/**
 * Connection-only health (internet, phone service, messaging). Reads only the
 * atoms the health helpers need — not the full diagnostics snapshot — so it
 * stays cheap to mount in the always-rendered nav.
 */
export function useConnectionHealth(): HealthLevel {
  const online = useOnlineStatus()
  const connectionStatus = useAtomValue(connectionStatusAtom)
  const registrationState = useAtomValue(registrationStatusAtom)
  const registrationError = useAtomValue(registrationErrorAtom)
  const transportState = useAtomValue(transportConnectionStateAtom)

  // Reuse the pure helpers by feeding them exactly the fields they read. Typed
  // as HealthInput (no cast) so the compiler verifies the shape — if a health
  // function starts reading a new field, this stops compiling instead of
  // silently passing undefined.
  const input: HealthInput = {
    network: { online },
    sip: {
      registrationState: String(registrationState),
      registrationError: registrationError?.message ?? null,
      transportState: String(transportState),
    },
    websocket: { connectionStatus },
  }

  return overallHealth([
    networkHealth(input),
    phoneServiceHealth(input),
    realtimeHealth(input),
  ])
}

/**
 * Overall app-health signal for consumers outside the status panel (the
 * top-nav indicator and account menu). Combines connection health with the
 * desktop App updates row, using the same pure helpers as the panel.
 */
export function useOverallHealth(): HealthLevel {
  const connection = useConnectionHealth()
  const appUpdates = useAppUpdatesHealth()

  return appUpdates ? worstLevel([connection, appUpdates.level]) : connection
}
